import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Spinner, Alert, Form } from 'react-bootstrap';
import { FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin, FiBriefcase, FiSave } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getAdminUserById, updateAdminUser } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { ADMIN_USER_ROLES, DEFAULT_ADMIN_COMPANY_NAME, normalizeAdminUserRole } from '../../constants/app';
import RoleDetailsButton from '../../components/Admin/RoleDetailsButton';

const AdminUserEdit = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [user, setUser] = useState(null);
    const isRefetchingRef = useRef(false);
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        company_name: '',
        country_code: '',
        role: '',
    });

    useEffect(() => {
        const fetchUser = async () => {
            if (!userId) return;
            
            try {
                setLoading(true);
                setError(null);
                const data = await getAdminUserById(userId);
                const userData = data?.user || data || null;
                setUser(userData);
                
                // Pré-remplir le formulaire avec les données de l'utilisateur
                if (userData) {
                    setFormData({
                        email: userData.email || '',
                        first_name: userData.first_name || '',
                        last_name: userData.last_name || '',
                        phone_number: userData.phone_number || '',
                        company_name: userData.company_name || DEFAULT_ADMIN_COMPANY_NAME,
                        country_code: userData.country_code || '',
                        role: normalizeAdminUserRole(userData.role),
                    });
                }
            } catch (e) {
                setError(handleApiError(e, t('pages.adminUserEdit.errorLoading')));
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Récupérer les données de l'utilisateur après une modification réussie
    // Meilleures pratiques : utiliser useRef pour éviter les appels multiples
    useEffect(() => {
        // Vérifier que success est défini, userId existe, et qu'on n'est pas déjà en train de recharger
        if (!success || !userId || isRefetchingRef.current) {
            return;
        }

        const refetchUser = async () => {
            isRefetchingRef.current = true;
            
            try {
                setRefreshing(true);
                // Petit délai pour laisser le temps à l'API de traiter la mise à jour
                await new Promise(resolve => setTimeout(resolve, 500));
                
                const data = await getAdminUserById(userId);
                const userData = data?.user || data || null;
                
                if (userData) {
                    setUser(userData);
                    
                    // Mettre à jour le formulaire avec les nouvelles données
                    setFormData({
                        email: userData.email || '',
                        first_name: userData.first_name || '',
                        last_name: userData.last_name || '',
                        phone_number: userData.phone_number || '',
                        company_name: userData.company_name || DEFAULT_ADMIN_COMPANY_NAME,
                        country_code: userData.country_code || '',
                        role: normalizeAdminUserRole(userData.role),
                    });
                }
            } catch (e) {
                // Ne pas écraser l'erreur de mise à jour si elle existe
                console.error('Erreur lors du rechargement de l\'utilisateur:', e);
                // Optionnel : afficher un message d'avertissement sans bloquer
                // setError(handleApiError(e, 'Erreur lors du rechargement de l\'utilisateur admin'));
            } finally {
                setRefreshing(false);
                isRefetchingRef.current = false;
            }
        };

        refetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, userId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Réinitialiser les erreurs lors de la modification
        if (error) setError(null);
        if (success) setSuccess(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);

            // Préparer les données pour l'API (enlever les champs vides optionnels)
            const submitData = {
                email: formData.email.trim(),
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
            };

            // Ajouter les champs optionnels seulement s'ils ne sont pas vides
            if (formData.phone_number.trim()) submitData.phone_number = formData.phone_number.trim();
            if (formData.company_name.trim()) submitData.company_name = formData.company_name.trim();
            if (formData.country_code.trim()) submitData.country_code = formData.country_code.trim();
            if (formData.role) submitData.role = formData.role;

            await updateAdminUser(userId, submitData);
                setSuccess(t('pages.adminUserEdit.success'));
        } catch (err) {
            setError(handleApiError(err, t('pages.adminUserEdit.error')));
        } finally {
            setLoading(false);
        }
    };

    if (loading && !user) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h2 className="page-title">
                                    <FiUser className="me-2" />
                                    {t('pages.adminUserEdit.title')}
                                </h2>
                            </div>
                            <Button variant="outline-secondary" onClick={() => navigate(`/admin/users/${userId}`)}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de l'utilisateur...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error && !user) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h2 className="page-title">
                                    <FiUser className="me-2" />
                                    {t('pages.adminUserEdit.title')}
                                </h2>
                            </div>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/management')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUser className="me-2" />
                                Modifier l'utilisateur administrateur
                            </h2>
                            <p className="text-muted mb-0">
                                {t('pages.adminUserEdit.subtitle', { name: `${user?.first_name} ${user?.last_name}` })}
                            </p>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <RoleDetailsButton selectedRole={formData.role} />
                            <Button variant="outline-secondary" onClick={() => navigate(`/admin/users/${userId}`)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-4">
                                <strong>{t('common.error')} :</strong> {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert variant="success" className="mb-4">
                            <div className="d-flex align-items-center">
                                <strong className="me-2">{t('common.success')} :</strong> {success}
                                {refreshing && (
                                    <Spinner size="sm" animation="border" variant="light" className="ms-2" />
                                )}
                            </div>
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit} key={user?.user_uuid || userId}>
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">{t('pages.adminUserEdit.mainInfo')}</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="exemple@email.com"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Téléphone
                                            </Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone_number"
                                                value={formData.phone_number}
                                                onChange={handleInputChange}
                                                placeholder="123456789"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Prénom <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleInputChange}
                                                required
                                                minLength={2}
                                                maxLength={100}
                                                placeholder="Prénom"
                                            />
                                            <Form.Text className="text-muted">
                                                Entre 2 et 100 caractères
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Nom <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                required
                                                minLength={2}
                                                maxLength={100}
                                                placeholder="Nom"
                                            />
                                            <Form.Text className="text-muted">
                                                Entre 2 et 100 caractères
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Entreprise</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="company_name"
                                                value={formData.company_name}
                                                onChange={handleInputChange}
                                                placeholder="Nom de l'entreprise"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code pays</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country_code"
                                                value={formData.country_code}
                                                onChange={handleInputChange}
                                                placeholder="Ex: +33"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Rôle <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Sélectionner un rôle</option>
                                                {ADMIN_USER_ROLES.map(({ value, label }) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(`/admin/users/${userId}`)}
                                disabled={loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={loading || refreshing}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        {t('pages.adminUserEdit.updating')}
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="me-2" />
                                        {t('pages.adminUserEdit.saveButton')}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminUserEdit;
